import { FunctionComponent, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/pro-regular-svg-icons';
import Button from 'components/UI/V3/Button/Button';
import Spinner from 'components/UI/V3/Spinner';
import CriterionAccordion from './CriterionAccordion';
import { useCriteriaCatalog } from '../../hooks/useCriteriaCatalog';
import { type CriterionValue, type Criterion } from '@spike/automation-model';
import styles from './CriteriaPanel.module.scss';

interface Props {
    selected: CriterionValue[];
    title?: string;
    definitions?: Criterion[];
    onBack: () => void;
    onConfirm: (criteria: CriterionValue[]) => void;
}

const CriteriaPanel: FunctionComponent<Props> = ({
    selected,
    title = 'Select criteria',
    definitions,
    onBack,
    onConfirm
}) => {
    const catalog = useCriteriaCatalog();

    const [values, setValues] = useState<Record<string, CriterionValue>>(() =>
        Object.fromEntries(selected.map(c => [c.field, c]))
    );

    const handleChange = (field: string, cv: CriterionValue | null) => {
        setValues(prev => {
            const next = { ...prev };
            if (cv === null) delete next[field];
            else next[field] = cv;
            return next;
        });
    };

    const grouped = definitions ? [{ category: 'custom', label: '', fields: definitions }] : catalog.grouped;

    if (!definitions && catalog.loading) {
        return (
            <div className={styles.panel}>
                <div className={styles.body}>
                    <Spinner />
                </div>
            </div>
        );
    }

    if (!definitions && catalog.unavailable) {
        return (
            <div className={styles.panel}>
                <div className={styles.header}>
                    <div
                        className={styles.backBtn}
                        aria-label="Go back"
                        onClick={onBack}
                    >
                        <FontAwesomeIcon
                            icon={faArrowLeft}
                            fontSize={24}
                        />
                    </div>
                    <span className={styles.title}>{title}</span>
                </div>
                <div className={styles.body}>
                    <p>Filters couldn&apos;t be loaded right now. Please try again in a moment.</p>
                </div>
                <div className={styles.footer}>
                    <Button
                        label="Close"
                        variant="outlined"
                        onClick={onBack}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className={styles.panel}>
            <div className={styles.header}>
                <div
                    className={styles.backBtn}
                    aria-label="Go back"
                    onClick={onBack}
                >
                    <FontAwesomeIcon
                        icon={faArrowLeft}
                        fontSize={24}
                        color="#00000"
                    />
                </div>
                <span className={styles.title}>{title}</span>
            </div>

            <div className={styles.body}>
                {grouped.map(group => (
                    <div
                        key={group.category}
                        className={styles.group}
                    >
                        {group.label && <div className={styles.groupLabel}>{group.label}</div>}
                        {group.fields.map(def => (
                            <CriterionAccordion
                                key={def.field}
                                definition={def}
                                value={values[def.field] ?? null}
                                operatorLabels={catalog.catalog?.operatorLabels}
                                onChange={cv => handleChange(def.field, cv)}
                            />
                        ))}
                    </div>
                ))}
            </div>

            <div className={styles.footer}>
                <Button
                    label="Cancel"
                    variant="outlined"
                    onClick={onBack}
                />
                <Button
                    label="Confirm"
                    variant="primary"
                    onClick={() => onConfirm(Object.values(values))}
                />
            </div>
        </div>
    );
};

export default CriteriaPanel;
